import { isString } from 'lodash';
import { AvailableTimes, RawAvailableTimes } from 'domains/calendars/types';
import { TimeZone } from 'domains/timezone';

/* @note(juan): the reason why we need this prefix is a bit tricky
 * The API responds with time slots grouped by day using UTC-7 for grouping
 *
 * Now, if you have a set of dates and you want to group them by day, you will get different groups
 * depending on what timezone you want to use. Because the start and end of a day will be a different UNIX timestamp depending on the timezone
 * For example 23:59 in Miami corresponds to a different UNIX time than 23:59 in Spain
 *
 * Note that API should also only give us dates as early as 7am US central and as late as 8pm
 *
 * In order to avoid timezone changes in the client side messing with the pre-made groups that come from the API we assume these groups are built with
 * the Hawaii offset (-10 UTC). If we don't do this and use the original -7 offset, changing timezone to Hawaii will make the groups jump one day backwards because
 * 00:00 in -7 offset corresponds to 21:00 in Hawaii -10 offset from the day before
 */
const TIMEZONE_OFFSET_PREFIX = 'T00:00:00-1000';

export const parseTimeSlotsData = (rawData: RawAvailableTimes): AvailableTimes => {
  return Object.entries(rawData).map(([day, times]) => ({
    day: new Date(`${day}${TIMEZONE_OFFSET_PREFIX}`),
    availableTimes: times.map(t => new Date(t)),
  }));
};

// @note(juan): The API uses some unconventional naming for timezones
export const API_TIMEZONE_MAP: Record<TimeZone, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Phoenix': 'Mountain (Arizona / No DST Observed)',
  'America/Los_Angeles': 'Pacific',
  'America/Anchorage': 'Alaskan',
  'Pacific/Honolulu': 'Hawaiian',
};

export const API_TIMEZONE_MAP_REVERSE: Record<string, TimeZone> = {
  'Eastern': 'America/New_York',
  'Central': 'America/Chicago',
  'Mountain': 'America/Denver',
  'Mountain (Arizona / No DST Observed)': 'America/Phoenix',
  'Pacific': 'America/Los_Angeles',
};

export const mapTimezoneToAPIName = (timezone: TimeZone): string => {
  const name = API_TIMEZONE_MAP[timezone];
  if (isString(name)) {
    return name;
  }
  throw new Error(`Tried to lookup timezone ${timezone} on API map but none found`);
};
